import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import MatchCard from './MatchCard';
import { Match, MatchStatus, Property, Client } from '@/types';
import { MatchingService } from '@/services/matchingService';
import { PropertyService } from '@/services/propertyService';
import { ClientService } from '@/services/clientService';

interface MatchListProps {
  matches?: Match[];
  loading?: boolean;
  error?: string | null;
  onMatchClick?: (match: Match) => void;
  onMatchAccept?: (match: Match) => void;
  onMatchReject?: (match: Match) => void;
  onMatchSchedule?: (match: Match) => void;
  filters?: {
    status?: MatchStatus[];
    minScore?: number;
    maxScore?: number;
  };
  onFilterChange?: (filters: { status?: MatchStatus[]; minScore?: number; maxScore?: number }) => void;
  pagination?: {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
}

const MatchList: React.FC<MatchListProps> = ({
  matches: externalMatches,
  loading: externalLoading,
  error: externalError,
  onMatchClick,
  onMatchAccept,
  onMatchReject,
  onMatchSchedule,
  filters: externalFilters,
  onFilterChange,
  pagination,
}) => {
  const [matches, setMatches] = useState<Match[]>(externalMatches || []);
  const [loading, setLoading] = useState(externalLoading || false);
  const [error, setError] = useState<string | null>(externalError || null);
  const [properties, setProperties] = useState<Map<string, Property>>(new Map());
  const [clients, setClients] = useState<Map<string, Client>>(new Map());
  const [filters, setFilters] = useState<{ status?: MatchStatus[]; minScore?: number; maxScore?: number }>(
    externalFilters || {}
  );
  const [statusFilter, setStatusFilter] = useState<MatchStatus[]>([]);
  const [currentPage, setCurrentPage] = useState(pagination?.page || 1);

  const isControlled = externalMatches !== undefined;

  useEffect(() => {
    if (externalMatches) {
      setMatches(externalMatches);
    }
  }, [externalMatches]);

  useEffect(() => {
    if (externalLoading !== undefined) {
      setLoading(externalLoading);
    }
  }, [externalLoading]);

  useEffect(() => {
    if (externalError !== undefined) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
      setStatusFilter(externalFilters.status || []);
    }
  }, [externalFilters]);

  // Fetch matches if not controlled
  useEffect(() => {
    if (!isControlled) {
      fetchMatches();
    }
  }, [currentPage, statusFilter]);

  // Load property and client data for matches
  useEffect(() => {
    if (matches.length > 0) {
      loadMatchData();
    }
  }, [matches]);

  const fetchMatches = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await MatchingService.getMatches(currentPage, 12);
      if (response.success && response.data) {
        let filteredMatches = response.data;

        // Apply status filter
        if (statusFilter.length > 0) {
          filteredMatches = filteredMatches.filter((m) => statusFilter.includes(m.status));
        }

        // Apply score filters
        if (filters.minScore !== undefined) {
          filteredMatches = filteredMatches.filter((m) => m.score.overall >= filters.minScore!);
        }
        if (filters.maxScore !== undefined) {
          filteredMatches = filteredMatches.filter((m) => m.score.overall <= filters.maxScore!);
        }

        setMatches(filteredMatches);
      } else {
        setError(response.error || 'Failed to load matches');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const loadMatchData = async () => {
    const propertyIds = new Set<string>();
    const clientIds = new Set<string>();

    matches.forEach((match) => {
      propertyIds.add(match.propertyId);
      clientIds.add(match.clientId);
    });

    // Load properties
    const propertyPromises = Array.from(propertyIds).map(async (id) => {
      try {
        const property = await PropertyService.getProperty(id);
        return property ? [id, property] as [string, Property] : null;
      } catch {
        return null;
      }
    });

    const propertyResults = await Promise.all(propertyPromises);
    const propertyMap = new Map<string, Property>();
    propertyResults.forEach((result) => {
      if (result) {
        propertyMap.set(result[0], result[1]);
      }
    });
    setProperties(propertyMap);

    // Load clients
    const clientPromises = Array.from(clientIds).map(async (id) => {
      try {
        const clientData = await ClientService.getClient(id);
        if (clientData) {
          return [id, clientData] as [string, Client];
        }
        return null;
      } catch {
        return null;
      }
    });

    const clientResults = await Promise.all(clientPromises);
    const clientMap = new Map<string, Client>();
    clientResults.forEach((result) => {
      if (result) {
        clientMap.set(result[0], result[1]);
      }
    });
    setClients(clientMap);
  };

  const handleStatusFilterChange = (newStatuses: MatchStatus[]) => {
    setStatusFilter(newStatuses);
    const newFilters = { ...filters, status: newStatuses.length > 0 ? newStatuses : undefined };
    setFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    if (pagination?.onPageChange) {
      pagination.onPageChange(page);
    }
  };

  const handleMatchAccept = async (match: Match) => {
    try {
      await MatchingService.updateMatchStatus(match.id, MatchStatus.INTERESTED);
      if (onMatchAccept) {
        onMatchAccept(match);
      }
      // Refresh matches
      if (!isControlled) {
        fetchMatches();
      }
    } catch (err: any) {
      alert(`Failed to update match: ${err.message}`);
    }
  };

  const handleMatchReject = async (match: Match) => {
    try {
      await MatchingService.updateMatchStatus(match.id, MatchStatus.NOT_INTERESTED);
      if (onMatchReject) {
        onMatchReject(match);
      }
      // Refresh matches
      if (!isControlled) {
        fetchMatches();
      }
    } catch (err: any) {
      alert(`Failed to update match: ${err.message}`);
    }
  };

  if (loading && matches.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!loading && matches.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No matches found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {statusFilter.length > 0
            ? 'Try adjusting your filters to see more results.'
            : 'Matches will appear here once properties and clients are matched.'}
        </Typography>
      </Box>
    );
  }

  const filteredMatches = matches.filter((match) => {
    if (statusFilter.length > 0 && !statusFilter.includes(match.status)) {
      return false;
    }
    if (filters.minScore !== undefined && match.score.overall < filters.minScore) {
      return false;
    }
    if (filters.maxScore !== undefined && match.score.overall > filters.maxScore) {
      return false;
    }
    return true;
  });

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={statusFilter}
            label="Status"
            onChange={(e) => handleStatusFilterChange(e.target.value as MatchStatus[])}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {Object.values(MatchStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          {filteredMatches.length} match{filteredMatches.length !== 1 ? 'es' : ''}
        </Typography>
      </Box>

      {/* Match Grid */}
      <Grid container spacing={3}>
        {filteredMatches.map((match) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={match.id}>
            <MatchCard
              match={match}
              property={properties.get(match.propertyId)}
              client={clients.get(match.clientId)}
              onView={onMatchClick}
              onAccept={handleMatchAccept}
              onReject={handleMatchReject}
              onSchedule={onMatchSchedule}
            />
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {pagination && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default MatchList;

